import React from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * PageTabBar — uniform horizontal tab navigation across hub pages.
 *
 * Phase C2 (2026-04-29): used to consolidate multiple sub-pages of a
 * functional area (Database, Etiquettes, Analytics, Stock) under a single
 * sidebar entry. URLs stay canonical (each tab is a real route), so all
 * existing bookmarks and direct links keep working.
 *
 * Props:
 *   tabs: [{ label: string, to: string, icon?: ReactNode, exact?: bool }, ...]
 *   activeColor: tailwind class (default amber)
 */
export default function PageTabBar({ tabs = [], activeColor = 'amber' }) {
  const { pathname } = useLocation();
  const colorMap = {
    amber: 'bg-amber-500/20 text-amber-300 border-amber-400',
    cyan: 'bg-cyan-500/20 text-cyan-300 border-cyan-400',
    emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-400',
    rose: 'bg-rose-500/20 text-rose-300 border-rose-400',
  };
  const activeClass = colorMap[activeColor] || colorMap.amber;

  const isActive = (tab) => {
    if (tab.exact) return pathname === tab.to;
    return pathname === tab.to || pathname.startsWith(tab.to + '/');
  };

  return (
    <div
      className="flex items-center gap-1 mb-4 border-b border-white/10 overflow-x-auto"
      data-testid="page-tabbar"
    >
      {tabs.map((tab) => {
        const active = isActive(tab);
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition flex items-center gap-2 whitespace-nowrap ${
              active
                ? `${activeClass} border-b-2`
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid={`page-tab-${tab.to.replace(/[^a-z0-9]/gi, '-')}`}
          >
            {tab.icon}
            {tab.label}
          </Link>
        );
      })}
    </div>
  );
}
